import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type ExternalWorkIntegrationStatusValues,
  SyncType,
  type WorkIntegrationResponse,
  type WorkIntegrationSyncRequest,
} from '@/common/api/types/workIntegrations';
import { isTerminalWorkIntegrationStatus } from '@/common/utils/workIntegrations';
import { invalidateInsightsData } from '@/features/insights-dashboard/hooks/invalidateInsightsData';
import { invalidateItemListQueries } from '@/features/items/hooks/invalidateItemListQueries';
import { removeVisibleTableCaches } from '@/features/items/hooks/removeVisibleTableCaches';
import { trackItemSyncInitiated } from '@/features/settings/work-integrations/analytics';
import {
  type SyncWorkIntegrationResult,
  WorkIntegrationsApi,
} from '@/features/settings/work-integrations/api/workIntegrations';
import { workIntegrationsQueryKeys } from '@/features/settings/work-integrations/queryKeys';

const findWorkIntegration = (
  queryClient: ReturnType<typeof useQueryClient>,
  workIntegrationId: number,
): WorkIntegrationResponse | undefined => {
  const queries = queryClient.getQueriesData<WorkIntegrationResponse[]>({
    queryKey: workIntegrationsQueryKeys.workIntegrations,
    predicate: (query) => typeof query.queryKey[1] !== 'string',
  });

  for (const [, data] of queries) {
    if (!Array.isArray(data)) continue;
    const found = data.find((workIntegration) => workIntegration.id === workIntegrationId);
    if (found) return found;
  }

  return undefined;
};

const updateWorkIntegrationStatusInAllQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  workIntegrationId: number,
  newStatus: ExternalWorkIntegrationStatusValues,
) => {
  const queries = queryClient.getQueriesData<WorkIntegrationResponse[]>({
    queryKey: workIntegrationsQueryKeys.workIntegrations,
  });

  queries.forEach(([queryKey, data]) => {
    if (!data || !Array.isArray(data)) return;

    queryClient.setQueryData<WorkIntegrationResponse[]>(queryKey, (oldData) => {
      if (!oldData || !Array.isArray(oldData)) return oldData;

      return oldData.map((workIntegration) =>
        workIntegration.id === workIntegrationId
          ? { ...workIntegration, status: newStatus }
          : workIntegration,
      );
    });
  });
};

export const useSyncWorkIntegrationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SyncWorkIntegrationResult,
    Error,
    { workIntegrationId: number; payload: WorkIntegrationSyncRequest }
  >({
    mutationFn: ({
      workIntegrationId,
      payload,
    }: {
      workIntegrationId: number;
      payload: WorkIntegrationSyncRequest;
    }) =>
      WorkIntegrationsApi.syncWorkIntegration({
        workIntegrationId,
        payload,
      }),
    onMutate: ({ workIntegrationId, payload }) => {
      const integration = findWorkIntegration(queryClient, workIntegrationId);
      if (!integration) return;

      trackItemSyncInitiated({
        loopId: integration.loopId,
        integrationId: integration.id,
        integrationSource: integration.sourceSystemType,
        integrationInstanceId: integration.instanceId,
        syncDirection: payload.syncType === SyncType.PULL ? 'pull' : 'push',
      });
    },
    onSuccess: (result) => {
      if (result.type === 'no-items' || result.type === 'no-field-mappings') {
        return;
      }

      updateWorkIntegrationStatusInAllQueries(
        queryClient,
        result.workIntegration.id,
        result.workIntegration.status,
      );

      if (isTerminalWorkIntegrationStatus(result.workIntegration.status)) {
        invalidateItemListQueries(queryClient);
        removeVisibleTableCaches(queryClient);
        invalidateInsightsData(queryClient);
        queryClient.invalidateQueries({
          queryKey: workIntegrationsQueryKeys.syncFailedItemsAll(),
        });
        queryClient.invalidateQueries({
          queryKey: workIntegrationsQueryKeys.pendingItemsCount({
            integrationId: result.workIntegration.id,
          }),
        });
      }

      queryClient.invalidateQueries({
        queryKey: workIntegrationsQueryKeys.workIntegrations,
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: workIntegrationsQueryKeys.workIntegrationStatus(),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: workIntegrationsQueryKeys.workIntegrations,
      });
    },
  });
};
